import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqData = [
  {
    question: "What is PROduce?",
    answer:
      "PROduce is a smart production tool designed for festival producers and their crew teams. It helps plan schedules, create briefings, track crews on-site, and communicate changes instantly—reducing stress and preventing miscommunication on show days.",
  },
  {
    question: "Who is PROduce designed for?",
    answer: `PROduce is built for:
• Festival producers
• Video and photo crews
• Social media creators
• Drone operators and FPV pilots
• Runners, ingest teams, editors
• Freelancers who want to manage their own festival planning independently

Both large teams and solo operators can use PROduce effectively.`,
  },
  {
    question: "What makes PROduce unique?",
    answer: `PROduce offers features not found in traditional scheduling tools:
• Live Tracking of crew members
• ETA alerts based on walking time to the next assignment
• AI-generated schedules (artists + custom subjects)
• AI editing: modify your schedule using natural language prompts
• Real-time push notifications when plans change
• Analytics dashboard for producers
• Instant briefing & planning generation (PDF + print-friendly)
• Crew-centric UX with personal planning and clear communication`,
  },
  {
    question: "Does PROduce work offline?",
    answer:
      "Yes. The native app supports offline mode with cached schedules and briefings, so crew members can continue working even without reception.",
  },
  {
    question: "How does Live Tracking work?",
    answer: `Live Tracking uses GPS to show where crew members are on the festival map.
Important details:
• GPS is only activated inside the festival zone, saving battery and respecting privacy.
• Producers can see real-time locations, walking ETA to assignments, and whether someone might be late.
• Crew members see their own path and receive urgency indicators.`,
  },
  {
    question: "What is the ETA warning system?",
    answer: `PROduce calculates how long it takes to walk from the crew member's current location to the next assignment.
If the ETA is longer than the remaining time, the system warns:
• The Live Tracker widget turns red
• The producer sees risk notifications
• Crew receives a "time is tight" push notification

This prevents delays and improves on-site coordination.`,
  },
  {
    question: "What can producers do with Analytics?",
    answer: `The analytics dashboard provides:
• Crew coverage per stage
• Bottleneck detection
• Overbooked or double-booked timeslots
• Idle times and break windows
• Stage heatmaps
• Historical data and performance indicators

Producers can export analytics as CSV and PDF, useful for debriefings, client reporting, and team optimization.`,
  },
  {
    question: "How do schedules work in PROduce?",
    answer: `Schedules can be created in several ways:

1. Manual scheduling
Producers assign:
• Artists from the lineup
• Custom subjects (e.g. Interviews, Crowd, BTS, Sponsor content)
• Any crew member(s)

2. AI-generated scheduling
You can:
• Generate a full schedule automatically
• Import a lineup → convert it to a structured list
• Ask AI to modify your schedule with natural language

All changes update instantly for all crew.`,
  },
  {
    question: "How do push notifications work?",
    answer: `Crew members receive instant notifications for:
• New assignments
• Updated times
• Cancellations
• Next-up reminders

This ensures everyone stays aligned without needing constant group chats.`,
  },
  {
    question: "How do briefings work in PROduce?",
    answer: `Briefings are generated automatically based on your schedule and production details. After publishing the production, briefings will be available in the crew member app.

You can:
• Export a PDF briefing
• Use a print-friendly mode
• Regenerate or adjust briefings at any time
• Include call times, team breakdowns, contact details, and maps`,
  },
  {
    question: "How fast can I plan a production?",
    answer: `With AI and auto-generation, a producer can create:
• Stages
• Lineup
• Full schedule
• Assignments
• Briefing
• Crew invitations

within 10 minutes from start to finish.`,
  },
  {
    question: "What is the full workflow from start to finish?",
    answer: `Typical flow:
1. Create a production
2. Add stages
3. Import lineup or add custom subjects
4. Generate schedule with AI, or create manually
5. Assign crew members to each block
6. Draw stage zones on the live tracking map
7. Invite crew (email invites + free crew accounts)
8. Generate briefing
9. Publish planning
10. Live day: real-time tracking, ETA warnings, personal planning, analytics
11. After event: export data, archived for future reference`,
  },
  {
    question: "How are crew members invited and managed?",
    answer: `Producers can:
• Invite crew via email
• Edit or reassign crew at any time
• Change roles
• Remove or replace crew
• Update assignments live

Crew accounts are free, always.`,
  },
  {
    question: "Can PROduce be used for multi-day festivals?",
    answer: "Yes. You can manage multiple days, stages, crews, and briefings in one project.",
  },
  {
    question: "What devices are supported?",
    answer: `PROduce works on:
• Crew — the PROduce Crew app on iOS and Android
• Producers — any modern browser

Crew work in the app, where push notifications, location sharing and offline access actually work.`,
  },
  {
    question: "What licenses does PROduce offer, and what are the limits?",
    answer: `PROduce offers multiple license tiers, all with a 7-day free trial:

LITE
• Price: €29/month or €290/year (7-day free trial)
• Productions: Unlimited
• Crew per production: Maximum 1
• Features: AI-powered scheduling, Real-time app updates

PRO
• Price: €99/month or €990/year (7-day free trial)
• Productions: 10 per year
• Crew per production: Up to 10
• Features: Everything in LITE + Live crew tracking

STUDIO
• Price: €199/month or €1990/year (7-day free trial)
• Productions: Unlimited
• Crew per production: Unlimited
• Features: Everything in PRO + Advanced analytics + White-glove support

General rules:
• Credits are only consumed when you publish a production
• Credits reset automatically at the start of each billing cycle
• Credits never roll over to the next period`,
  },
  {
    question: "Can freelancers use PROduce for personal planning?",
    answer: `Yes! Independent camera operators and photographers with the LITE plan can:
• Create their own personal schedule
• Plan multiple stages
• Add notes or custom blocks
• View their entire day clearly
• Use the AI schedule assistant

Perfect for freelancers working event-to-event.`,
  },
  {
    question: "Is my data private and secure?",
    answer:
      "Yes. GPS is only active inside the festival zone. Data is encrypted and processed securely.",
  },
];

const FAQSection = () => {
  return (
    <section id="faq" className="py-24" style={{ backgroundColor: "#050e22" }}>
      <div className="container mx-auto px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold">Frequently asked questions</h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Everything you need to know about PROduce
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="rounded-lg border border-border bg-card/50 px-6 backdrop-blur-xl transition-colors data-[state=open]:border-primary/50"
              >
                <AccordionTrigger className="py-5 text-left hover:no-underline">
                  <span className="font-semibold text-foreground">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="whitespace-pre-line pb-5 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
